import { KeyboardEvent, useState } from 'react';
import Button from '../../ui/Button';

// The form's generic question adds nothing under a section already titled Comments.
const label = (question: string) => (question.trim().toLowerCase() === 'comments' ? '' : question.trim());

export interface CarouselComment {
  question: string;
  text: string;
}

/**
 * One comment at a time, moved by hand (buttons or arrow keys), never on a timer. "Show all" gives the
 * plain list back for scanning every comment at once.
 */
export default function CommentCarousel({ comments }: { comments: CarouselComment[] }) {
  const [index, setIndex] = useState(0);
  const [all, setAll] = useState(false);
  const n = comments.length;
  if (!n) return null;
  const move = (step: number) => setIndex((i) => (i + step + n) % n);
  const onKey = (e: KeyboardEvent) => {
    if (e.key === 'ArrowRight') move(1);
    else if (e.key === 'ArrowLeft') move(-1);
    else return;
    e.preventDefault();
  };
  const current = comments[index];
  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <span className="text-ink-2" aria-live="polite">
          {all ? `${n} comments` : `${index + 1} of ${n}`}
        </span>
        <div className="flex gap-2">
          {all ? null : (
            <>
              <Button size="sm" variant="ghost" onClick={() => move(-1)} aria-label="Previous comment">
                ←
              </Button>
              <Button size="sm" variant="ghost" onClick={() => move(1)} aria-label="Next comment">
                →
              </Button>
            </>
          )}
          <Button size="sm" variant="ghost" onClick={() => setAll((v) => !v)}>
            {all ? 'One at a time' : `Show all ${n}`}
          </Button>
        </div>
      </div>
      {all ? (
        <ul className="max-h-[320px] overflow-auto">
          {comments.map((c, i) => (
            <li key={i} className="border-b border-line py-[4px]">
              {label(c.question) ? <span className="text-ink-2">{label(c.question)}: </span> : null}
              {c.text}
            </li>
          ))}
        </ul>
      ) : (
        <div
          role="group"
          aria-roledescription="carousel"
          aria-label="Comments"
          tabIndex={0}
          onKeyDown={onKey}
          className="border-l border-line-strong pl-4 py-2 min-h-[96px] focus:outline-none focus-visible:ring-2 focus-visible:ring-ink"
        >
          {label(current.question) ? <div className="text-small text-ink-2 mb-1">{label(current.question)}</div> : null}
          <p className="text-[15px] leading-snug">{current.text}</p>
        </div>
      )}
    </div>
  );
}
